/**
 * TendrilStatusChecklist — the "thinking" trace for a streaming Tendril turn
 * (tendril-streaming-responses Phase 3 WI-008, restyled after the first live UAT).
 *
 * Renders ABOVE the answer, deliberately: the trace is what Tendril did in order
 * to produce the reply, so reading top-to-bottom follows the actual sequence.
 * The first implementation rendered it below and it read like a footnote.
 *
 * Visual intent: de-emphasised, quiet, secondary — closer to a collapsed
 * "thoughts" block than to message content. It must never compete with the
 * answer for attention, which is why there are no bullets, no borders around
 * individual rows, muted small text, and a single hairline rule tying the steps
 * together.
 *
 * Each capability is ONE step that transitions active → done (the pairing is
 * done in useTendrilChat's onStatus/onTool handlers). Active steps show a
 * pulsing dot; completed steps show a check.
 *
 * Accessibility: this whole region is decorative narration of work in progress.
 * It is marked aria-hidden so screen readers are not spammed with a dozen
 * transient step announcements — TendrilStatusAnnouncer owns the accessible
 * narration, announcing only discrete transitions once each. The toggle button
 * itself stays reachable and labelled.
 *
 * BEM: .tendril-thinking, .tendril-thinking__toggle, .tendril-thinking__steps,
 *      .tendril-thinking__step, .tendril-thinking__marker, .tendril-thinking__label
 */
import React, { useState } from 'react';

/**
 * @param {{
 *   statusLines: Array<{ id: string, label: string, status: 'active' | 'done' }>,
 *   hasFirstToken: boolean,
 *   elapsedSeconds: number,
 * }} props
 */
export default function TendrilStatusChecklist({ statusLines, hasFirstToken, elapsedSeconds }) {
  const [manuallyExpanded, setManuallyExpanded] = useState(false);

  if (!statusLines || statusLines.length === 0) return null;

  // Expanded while the tool phase is still running (that IS the progress
  // indicator), or when the user opts back in after it collapses.
  const expanded = !hasFirstToken || manuallyExpanded;
  const count = statusLines.length;
  const noun = count === 1 ? 'step' : 'steps';

  const summary = hasFirstToken
    ? `${count} ${noun} · ${elapsedSeconds}s`
    : `Working · ${count} ${noun}`;

  return (
    <div className={`tendril-thinking${expanded ? ' tendril-thinking--expanded' : ''}`}>
      {/* Only interactive once collapsing is meaningful (i.e. the answer has
          started). While still working, this is a plain non-interactive label —
          collapsing mid-flight would hide the only progress feedback there is. */}
      {hasFirstToken ? (
        <button
          type="button"
          className="tendril-thinking__toggle"
          onClick={() => setManuallyExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          <span className="tendril-thinking__chevron" aria-hidden="true">
            {expanded ? '▾' : '▸'}
          </span>
          <span>{expanded ? 'Hide steps' : summary}</span>
        </button>
      ) : (
        <div className="tendril-thinking__toggle tendril-thinking__toggle--static">
          <span className="tendril-thinking__spinner" aria-hidden="true" />
          <span>{summary}</span>
        </div>
      )}

      {expanded && (
        <ul className="tendril-thinking__steps" aria-hidden="true">
          {statusLines.map((line) => (
            <li
              key={line.id}
              className={[
                'tendril-thinking__step',
                line.status === 'done'
                  ? 'tendril-thinking__step--done'
                  : 'tendril-thinking__step--active',
              ].join(' ')}
            >
              <span className="tendril-thinking__marker" aria-hidden="true">
                {line.status === 'done' ? '✓' : ''}
              </span>
              <span className="tendril-thinking__label">{line.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
